// The top-bar assistant button's face: the 3D mascot head, following the assistant session like the
// full body in the drawer. three.js and the head GLB (≈0.5 MB) load only once the page is idle; until
// the head is on screen (or if WebGL fails) the 2D AI bot (D-043) holds the spot, at the same size.
// Hovering or focusing the button (`.ady-bot-host`) makes the head smile.
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { cx } from "@ady/ui";
import { mascotStateFor, setAssistantStatus, useAssistantSession } from "./assistantSession";
import { endGreeting, setHeadReady, useGreeting } from "./greeting";

import { RootHead } from "./RootHead";
const AdyvanceMascotHead = lazy(() => import("./AdyvanceMascotHead"));

function whenIdle(fn: () => void): () => void {
  if (typeof requestIdleCallback === "function") {
    const id = requestIdleCallback(fn, { timeout: 2500 });
    return () => cancelIdleCallback(id);
  }
  const id = window.setTimeout(fn, 1200);
  return () => window.clearTimeout(id);
}

export function HeaderMascot({ size = 36 }: { size?: number }) {
  const session = useAssistantSession();
  const { phase } = useGreeting();
  // The "Hi" greeting (AssistantHello) briefly takes over: a beat of talking, then the happy nod.
  const state =
    phase === "talking" ? "talking" : phase === "nod" ? "done" : mascotStateFor(session.status);
  const [load, setLoad] = useState(false);
  const [ready, setReady] = useState(false);
  const [hover, setHover] = useState(false);
  const box = useRef<HTMLSpanElement>(null);

  useEffect(() => whenIdle(() => setLoad(true)), []);

  useEffect(() => {
    const host = box.current?.closest<HTMLElement>(".ady-bot-host");
    if (!host) return;
    const on = () => setHover(true);
    const off = () => setHover(false);
    host.addEventListener("pointerenter", on);
    host.addEventListener("pointerleave", off);
    host.addEventListener("focus", on);
    host.addEventListener("blur", off);
    return () => {
      host.removeEventListener("pointerenter", on);
      host.removeEventListener("pointerleave", off);
      host.removeEventListener("focus", on);
      host.removeEventListener("blur", off);
    };
  }, []);

  return (
    <span
      ref={box}
      className="relative flex items-center justify-center"
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <span
        className={cx("absolute inset-0 flex items-center justify-center", ready && "invisible")}
      >
        <RootHead size={Math.round(size * 0.9)} />
      </span>
      {load ? (
        <Suspense fallback={null}>
          <span className={cx("absolute inset-0", !ready && "opacity-0")}>
            <AdyvanceMascotHead
              size={size}
              state={state}
              speech={session.speech}
              reaction={hover}
              onReady={() => {
                setReady(true);
                setHeadReady();
              }}
              onDoneComplete={() => {
                endGreeting();
                if (session.status === "completed") setAssistantStatus("ready");
              }}
            />
          </span>
        </Suspense>
      ) : null}
    </span>
  );
}
